import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AtelierLogoField } from '../components/AtelierLogoField';
import { PriceChartSection } from '../components/PriceChartSection';
import { Button } from '../../components/ui/button';
import {
  DollarSign, FileText, Users, Plus, Search,
  Eye, Download, ArrowLeft, Settings2, Trash2,
  Clock, CheckCircle, AlertTriangle, Send
} from 'lucide-react';
import { useMechanicAuth } from '../MechanicAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rateTypes = [
  { value: 'per_hour', label: 'A l\'heure' },
  { value: 'per_km', label: 'Au km' },
  { value: 'flat', label: 'Forfaitaire' },
  { value: 'per_unit', label: 'A l\'unite' },
];

export default function MechanicBillingPage() {
  const { token } = useMechanicAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [invRes, cliRes, dashRes] = await Promise.all([
        axios.get(`${API_URL}/api/atelier-billing/invoices`, { headers }),
        axios.get(`${API_URL}/api/atelier-billing/clients`, { headers }),
        axios.get(`${API_URL}/api/atelier-billing/dashboard`, { headers }),
      ]);
      setInvoices(invRes.data);
      setClients(cliRes.data);
      setDashboard(dashRes.data);
    } catch (e) { console.error(e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { if (token) fetchData(); }, [fetchData, token]);

  const openInvoice = (inv) => { setSelectedInvoice(inv); setActiveTab('detail'); };

  if (activeTab === 'detail' && selectedInvoice) {
    return <InvoiceDetailTab invoice={selectedInvoice} setInvoice={setSelectedInvoice}
      clients={clients} token={token} onBack={() => { setActiveTab('invoices'); fetchData(); }}
      onDelete={() => { setActiveTab('invoices'); fetchData(); }} />;
  }

  if (activeTab === 'clients') {
    return <ClientsTab clients={clients} token={token} onRefresh={fetchData} onBack={() => setActiveTab('dashboard')} />;
  }

  if (activeTab === 'new') {
    return <NewInvoiceTab clients={clients} token={token}
      onDone={(inv) => { openInvoice(inv); fetchData(); }} onBack={() => setActiveTab('dashboard')} />;
  }

  if (activeTab === 'taxconfig') {
    return <TaxConfigTab token={token} onBack={() => setActiveTab('dashboard')} />;
  }

  if (activeTab === 'invoiceconfig') {
    return <InvoiceConfigTab token={token} onBack={() => setActiveTab('dashboard')} />;
  }

  // ==================== DASHBOARD + INVOICES LIST ====================
  return (
    <div className="min-h-screen bg-slate-950 pb-20 px-4 pt-4" data-testid="mechanic-billing-page">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-white">Facturation Atelier</h1>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
              onClick={() => setActiveTab('clients')} data-testid="atelier-clients-btn">
              <Users size={14} className="mr-1" /> Clients
            </Button>
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
              onClick={() => setActiveTab('taxconfig')} data-testid="atelier-taxconfig-btn">
              <Settings2 size={14} className="mr-1" /> Taxes
            </Button>
            <Button size="sm" variant="outline" className="border-slate-700 text-slate-300"
              onClick={() => setActiveTab('invoiceconfig')} data-testid="atelier-invoiceconfig-btn">
              <Settings2 size={14} className="mr-1" /> Config
            </Button>
          </div>
        </div>

        {/* Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Brouillons', value: `${dashboard.total_draft?.toFixed(2)} $`, count: dashboard.by_status?.draft, color: 'slate' },
              { label: 'En attente', value: `${dashboard.total_pending?.toFixed(2)} $`, count: dashboard.by_status?.pending_approval, color: 'amber' },
              { label: 'Envoyees', value: `${dashboard.total_sent?.toFixed(2)} $`, count: dashboard.by_status?.sent, color: 'blue' },
              { label: 'Payees', value: `${dashboard.total_paid?.toFixed(2)} $`, count: dashboard.by_status?.paid, color: 'green' },
            ].map(s => (
              <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-xl p-3">
                <p className="text-xs text-slate-400">{s.label} ({s.count || 0})</p>
                <p className="text-lg font-bold text-white">{s.value}</p>
              </div>
            ))}
          </div>
        )}

        <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
          onClick={() => setActiveTab('new')} data-testid="new-atelier-invoice-btn">
          <Plus size={16} className="mr-2" /> Nouvelle facture
        </Button>

        {/* Invoices list */}
        <div className="space-y-2">
          <h2 className="text-sm text-slate-400 font-medium">Factures recentes</h2>
          {invoices.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Aucune facture</p>}
          {invoices.map(inv => (
            <div key={inv.id} onClick={() => openInvoice(inv)}
              className="bg-slate-900 border border-slate-800 rounded-xl p-3 cursor-pointer hover:border-amber-500/30 transition-colors"
              data-testid={`invoice-${inv.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-white font-medium text-sm">{inv.custom_invoice_number || inv.invoice_number}</p>
                  <p className="text-xs text-slate-400">{inv.client_name || 'Sans client'} — {inv.date}</p>
                  {inv.work_order_number && <p className="text-xs text-slate-500">BT: {inv.work_order_number}</p>}
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-sm">{inv.total?.toFixed(2)} $</p>
                  <StatusBadge status={inv.status} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function StatusBadge({ status }) {
  const map = {
    draft: { label: 'Brouillon', cls: 'bg-slate-700 text-slate-300' },
    pending_approval: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
    approved: { label: 'Approuvee', cls: 'bg-green-500/20 text-green-400' },
    sent: { label: 'Envoyee', cls: 'bg-blue-500/20 text-blue-400' },
    paid: { label: 'Payee', cls: 'bg-green-500/20 text-green-400' },
  };
  const s = map[status] || map.draft;
  return <span className={`text-xs px-2 py-0.5 rounded-full ${s.cls}`} data-testid="status-badge">{s.label}</span>;
}


// ============================
// CLIENTS TAB
// ============================
function ClientsTab({ clients: initialClients, token, onRefresh, onBack }) {
  const [clients, setClients] = useState(initialClients);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    name: '', address: '', city: '', province: 'QC', postal_code: '',
    email: '', phone: '', tps_number: '', tvq_number: '', notes: '',
    default_rate_type: 'per_hour', default_rate_value: 0, rate_presets: [],
  });
  const headers = { Authorization: `Bearer ${token}` };
  const [savingClient, setSavingClient] = useState(false);

  const handleSubmit = async () => {
    setSavingClient(true);
    const vd = form.volume_discount_percent;
    const payload = { ...form, volume_discount_percent: vd === '' || vd == null ? 0 : Number(vd) };
    delete payload.parts_markup_percent;
    try {
      if (editId) {
        await axios.put(`${API_URL}/api/atelier-billing/clients/${editId}`, payload, { headers });
        toast.success('Client modifie');
      } else {
        await axios.post(`${API_URL}/api/atelier-billing/clients`, payload, { headers });
        toast.success('Client ajoute');
      }
      const res = await axios.get(`${API_URL}/api/atelier-billing/clients`, { headers });
      setClients(res.data);
      setShowForm(false); setEditId(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingClient(false); }
  };

  const handleEdit = (c) => {
    setForm({
      name: c.name || '', address: c.address || '', city: c.city || '',
      province: c.province || 'QC', postal_code: c.postal_code || '',
      email: c.email || '', phone: c.phone || '', tps_number: c.tps_number || '',
      tvq_number: c.tvq_number || '', notes: c.notes || '',
      default_rate_type: c.default_rate_type || 'per_hour',
      default_rate_value: c.default_rate_value || 0,
      rate_presets: c.rate_presets || [],
      volume_discount_percent: c.volume_discount_percent ?? '',
    });
    setEditId(c.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce client?')) return;
    try {
      await axios.delete(`${API_URL}/api/atelier-billing/clients/${id}`, { headers });
      setClients(prev => prev.filter(c => c.id !== id));
      toast.success('Client supprime');
    } catch (e) { toast.error('Erreur'); }
  };

  const addPreset = () => {
    setForm(prev => ({
      ...prev,
      rate_presets: [...prev.rate_presets, { name: '', type: 'per_hour', value: 0 }],
    }));
  };

  const updatePreset = (idx, field, value) => {
    const presets = [...form.rate_presets];
    presets[idx] = { ...presets[idx], [field]: field === 'value' ? parseFloat(value) || 0 : value };
    setForm(prev => ({ ...prev, rate_presets: presets }));
  };

  const removePreset = (idx) => {
    setForm(prev => ({ ...prev, rate_presets: prev.rate_presets.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="min-h-screen bg-slate-950 pb-20 px-4 pt-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <ArrowLeft size={16} /> Retour
          </button>
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-black"
            onClick={() => { setShowForm(true); setEditId(null); setForm({name:'',address:'',city:'',province:'QC',postal_code:'',email:'',phone:'',tps_number:'',tvq_number:'',notes:'',default_rate_type:'per_hour',default_rate_value:0,rate_presets:[]}); }}
            data-testid="add-atelier-client-btn">
            <Plus size={14} className="mr-1" /> Ajouter
          </Button>
        </div>

        <h2 className="text-lg font-bold text-white">Clients Atelier</h2>

        {/* Client list */}
        {clients.map(c => (
          <div key={c.id} className="bg-slate-900 border border-slate-800 rounded-xl p-3">
            <div className="flex justify-between items-start">
              <div className="cursor-pointer flex-1" onClick={() => handleEdit(c)}>
                <p className="text-white font-medium text-sm">{c.name}</p>
                <div className="flex items-center gap-2 mt-1">
                  {Number(c.volume_discount_percent) > 0 && <span className="text-xs bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded" data-testid={`client-discount-badge-${c.id}`}>-{c.volume_discount_percent} %</span>}
                  {(c.rate_presets || []).length > 0 && <span className="text-xs bg-amber-500/10 text-amber-400 px-2 py-0.5 rounded">{c.rate_presets.length} prereglage{c.rate_presets.length > 1 ? 's' : ''}</span>}
                  <span className="text-xs text-slate-500">{rateTypes.find(r => r.value === c.default_rate_type)?.label} — {c.default_rate_value}$</span>
                </div>
              </div>
              <button onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-300 p-1" data-testid={`delete-client-${c.id}`}>
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}

        {clients.length === 0 && <p className="text-sm text-slate-500 text-center py-6">Aucun client</p>}

        {/* Client form modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center sm:items-center">
            <div className="bg-slate-900 border border-slate-700 rounded-t-2xl sm:rounded-2xl p-5 w-full max-w-lg max-h-[85vh] overflow-y-auto space-y-4">
              <h3 className="text-white font-bold">{editId ? 'Modifier client' : 'Nouveau client'}</h3>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'name', label: 'Nom', span: 2 },
                  { key: 'address', label: 'Adresse', span: 2 },
                  { key: 'city', label: 'Ville' },
                  { key: 'province', label: 'Province' },
                  { key: 'postal_code', label: 'Code postal' },
                  { key: 'email', label: 'Courriel' },
                  { key: 'phone', label: 'Telephone' },
                ].map(f => (
                  <div key={f.key} className={f.span === 2 ? 'col-span-2' : ''}>
                    <label className="text-xs text-slate-400 block mb-1">{f.label}</label>
                    <input type="text" value={form[f.key] || ''} onChange={e => setForm(prev => ({...prev, [f.key]: e.target.value}))}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                      data-testid={`client-${f.key}`} />
                  </div>
                ))}
              </div>
              <div>
                <label className="text-xs text-slate-400 block mb-1">Réduction fidélité / volume (%)</label>
                <input type="number" step="0.5" min="0" max="100" value={form.volume_discount_percent ?? ''} placeholder="0 = aucune réduction"
                  onChange={e => setForm(prev => ({ ...prev, volume_discount_percent: e.target.value }))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  data-testid="client-volume-discount" />
                <p className="text-[11px] text-slate-500 mt-1">Appliquée sur le sous-total avant taxes. Les prix suivent la charte de l'atelier.</p>
              </div>

              {/* Rate presets */}
              <div className="border-t border-slate-700 pt-3 space-y-2">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-amber-400 font-semibold uppercase">Préréglages tarifaires</p>
                  <button onClick={addPreset} className="text-xs text-amber-400 hover:text-amber-300" data-testid="add-preset-btn">+ Ajouter</button>
                </div>
                {form.rate_presets.map((preset, idx) => (
                  <div key={idx} className="flex gap-2 items-center bg-slate-800 rounded-lg p-2">
                    <input type="text" placeholder="Nom" value={preset.name} onChange={e => updatePreset(idx, 'name', e.target.value)} data-testid={`preset-name-${idx}`}
                      className="flex-1 bg-transparent border-b border-slate-600 text-white text-xs px-1 py-1 focus:outline-none focus:border-amber-500" />
                    <select value={preset.type} onChange={e => updatePreset(idx, 'type', e.target.value)}
                      className="bg-slate-700 text-white text-xs rounded px-2 py-1">
                      {rateTypes.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <input type="number" value={preset.value} onChange={e => updatePreset(idx, 'value', e.target.value)} data-testid={`preset-value-${idx}`}
                      className="w-16 bg-transparent border-b border-slate-600 text-white text-xs px-1 py-1 text-right focus:outline-none focus:border-amber-500" />
                    <span className="text-xs text-slate-400">$</span>
                    <button onClick={() => removePreset(idx)} className="text-red-400 hover:text-red-300" data-testid={`preset-remove-${idx}`}><Trash2 size={12} /></button>
                  </div>
                ))}
              </div>

              <div className="flex gap-3">
                <Button className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" onClick={handleSubmit} disabled={savingClient} data-testid="save-client-btn">
                  {savingClient ? 'Enregistrement…' : editId ? 'Modifier' : 'Ajouter'}
                </Button>
                <Button variant="outline" className="border-slate-700 text-slate-300" onClick={() => setShowForm(false)}>
                  Annuler
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ============================
// NEW INVOICE TAB
// ============================
function NewInvoiceTab({ clients, token, onDone, onBack }) {
  const [mode, setMode] = useState('work_order'); // work_order | manual
  const [workOrders, setWorkOrders] = useState([]);
  const [selectedWO, setSelectedWO] = useState(null);
  const [clientId, setClientId] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/atelier-billing/available-work-orders`, { headers })
      .then(r => setWorkOrders(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const createInvoice = async () => {
    setCreating(true);
    try {
      const body = { client_id: clientId || null };
      if (mode === 'work_order' && selectedWO) {
        body.work_order_id = selectedWO.id;
      } else {
        body.description = description || 'Service';
      }
      const res = await axios.post(`${API_URL}/api/atelier-billing/invoices/generate`, body, { headers });
      toast.success('Facture creee');
      onDone(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setCreating(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 pb-20 px-4 pt-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Retour
        </button>
        <h2 className="text-lg font-bold text-white">Nouvelle facture</h2>

        {/* Mode selector */}
        <div className="flex gap-2">
          <button onClick={() => setMode('work_order')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'work_order' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}
            data-testid="mode-work-order">
            Depuis un bon de travail
          </button>
          <button onClick={() => setMode('manual')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${mode === 'manual' ? 'bg-amber-500 text-black' : 'bg-slate-800 text-slate-300'}`}
            data-testid="mode-manual">
            Creation manuelle
          </button>
        </div>

        {/* Work order selection */}
        {mode === 'work_order' && (
          <div className="space-y-2">
            <p className="text-xs text-slate-400">Bons de travail completes disponibles</p>
            {workOrders.length === 0 && <p className="text-sm text-slate-500 text-center py-4">Aucun bon de travail disponible</p>}
            {workOrders.map(wo => (
              <div key={wo.id} onClick={() => setSelectedWO(wo)}
                className={`bg-slate-900 border rounded-xl p-3 cursor-pointer transition-colors ${selectedWO?.id === wo.id ? 'border-amber-500' : 'border-slate-800 hover:border-slate-600'}`}
                data-testid={`wo-${wo.id}`}>
                <p className="text-white font-medium text-sm">{wo.order_number}</p>
                <p className="text-xs text-slate-400">{wo.client_name || 'Sans client'} — {wo.vehicle_brand} {wo.vehicle_model}</p>
                <p className="text-xs text-slate-500">{(wo.tasks || []).length} tache(s)</p>
              </div>
            ))}
          </div>
        )}

        {/* Manual description */}
        {mode === 'manual' && (
          <div>
            <label className="text-xs text-slate-400 block mb-1">Description du service</label>
            <input type="text" value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Ex: Service routier, reparation frein..."
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="manual-description" />
          </div>
        )}

        {/* Client selection */}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Client (optionnel)</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
            data-testid="client-select">
            <option value="">-- Selectionner un client --</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
          onClick={createInvoice} disabled={creating || (mode === 'work_order' && !selectedWO)}
          data-testid="create-invoice-btn">
          {creating ? 'Creation...' : 'Creer la facture'}
        </Button>
      </div>
    </div>
  );
}


// ============================
// INVOICE DETAIL TAB
// ============================
function InvoiceDetailTab({ invoice: initialInvoice, setInvoice: setParentInvoice, clients, token, onBack, onDelete }) {
  const [invoice, setInvoice] = useState(initialInvoice);
  const [saving, setSaving] = useState(false);
  const [appliedPresetName, setAppliedPresetName] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const [chart, setChart] = useState([]);
  useEffect(() => {
    axios.get(`${API_URL}/api/atelier-billing/invoice-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const c = r.data || {};
        const base = Number(c.labor_rate) > 0 ? [{ name: 'Taux horaire atelier', type: 'per_hour', value: Number(c.labor_rate) }] : [];
        setChart([...base, ...(c.price_chart || [])]);
      }).catch(() => {});
  }, [token]);
  const selectedClient = clients.find(c => c.id === invoice.client_id);
  const clientPresets = [...chart, ...(selectedClient?.rate_presets || [])];

  const saveInvoice = async (extra = {}) => {
    setSaving(true);
    try {
      const data = {
        client_id: invoice.client_id,
        client_name: invoice.client_name,
        client_address: invoice.client_address,
        client_email: invoice.client_email,
        discount_percent: Number(invoice.discount_percent) || 0,
        rate_type: invoice.rate_type,
        rate_value: invoice.rate_value,
        line_items: invoice.line_items,
        taxes_enabled: invoice.taxes_enabled,
        applied_taxes: invoice.applied_taxes,
        notes: invoice.notes,
        custom_invoice_number: invoice.custom_invoice_number,
        ...extra,
      };
      const res = await axios.put(`${API_URL}/api/atelier-billing/invoices/${invoice.id}`, data, { headers });
      setInvoice(res.data);
      setParentInvoice(res.data);
      toast.success('Facture sauvegardee');
    } catch (e) { toast.error('Erreur de sauvegarde'); }
    setSaving(false);
  };

  const updateLineItem = (index, field, value) => {
    const items = [...(invoice.line_items || [])];
    items[index] = { ...items[index], [field]: value };
    if (field === 'quantity' || field === 'unit_price') {
      items[index].amount = Math.round((items[index].quantity || 0) * (items[index].unit_price || 0) * 100) / 100;
    }
    setInvoice(prev => ({ ...prev, line_items: items }));
  };

  const addLineItem = () => {
    setInvoice(prev => ({
      ...prev,
      line_items: [...(prev.line_items || []), { description: '', quantity: 1, unit_price: 0, amount: 0 }],
    }));
  };

  const removeLineItem = (idx) => {
    setInvoice(prev => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));
  };

  const applyPreset = (preset) => {
    const items = (invoice.line_items || []).map(i => ({ ...i }));
    const labor = items.filter(i => i.kind === 'labor');
    (labor.length ? labor : items).forEach(item => {
      if (preset.type === 'per_hour') item.quantity = 1;
      else if (preset.type === 'per_unit') item.quantity = 1;
      else item.quantity = 1;
      item.unit_price = preset.value || 0;
      item.amount = Math.round(item.quantity * item.unit_price * 100) / 100;
    });
    setInvoice(prev => ({ ...prev, rate_type: preset.type, rate_value: preset.value, line_items: items }));
    setAppliedPresetName(preset.name);
    toast.success(`Prereglage "${preset.name}" applique`);
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (client) {
      const addrParts = [client.address];
      const cityParts = [client.city, client.province, client.postal_code].filter(Boolean);
      if (cityParts.length) addrParts.push(cityParts.join(', '));
      setInvoice(prev => ({
        ...prev,
        client_id: clientId,
        client_name: client.name,
        client_address: addrParts.filter(Boolean).join(', '),
        client_email: client.email || '',
        discount_percent: Number(client.volume_discount_percent) || 0,
      }));
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/atelier-billing/invoices/${invoice.id}/pdf`, {
        headers, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank');
    } catch (e) { toast.error('Erreur PDF'); }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette facture?')) return;
    try {
      await axios.delete(`${API_URL}/api/atelier-billing/invoices/${invoice.id}`, { headers });
      toast.success('Facture supprimee');
      onDelete();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const submitForApproval = async () => {
    try {
      await axios.post(`${API_URL}/api/atelier-billing/invoices/${invoice.id}/submit`, {}, { headers });
      setInvoice(prev => ({ ...prev, status: 'pending_approval' }));
      toast.success('Soumise pour approbation');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const subtotal = (invoice.line_items || []).reduce((s, i) => s + (i.amount || 0), 0);
  const discountPct = Number(invoice.discount_percent) || 0;
  const discount = Math.round(subtotal * discountPct) / 100;
  const taxable = subtotal - discount;

  return (
    <div className="min-h-screen bg-slate-950 pb-20 px-4 pt-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex justify-between items-center">
          <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="flex gap-2">
            <StatusBadge status={invoice.status} />
            <button onClick={downloadPdf} className="text-blue-400 hover:text-blue-300" data-testid="download-pdf-btn">
              <Download size={16} />
            </button>
          </div>
        </div>

        {invoice.work_order_number && (
          <div className={`rounded-xl border p-3 text-xs ${invoice.auto_warnings?.length ? 'border-amber-500/40 bg-amber-500/10' : 'border-emerald-500/30 bg-emerald-500/10'}`} data-testid="invoice-auto-banner">
            <p className={invoice.auto_warnings?.length ? 'text-amber-300 font-semibold' : 'text-emerald-300 font-semibold'}>
              {invoice.auto_generated ? 'Générée automatiquement' : 'Créée'} depuis le bon {invoice.work_order_number}
              {invoice.parts_markup_percent ? ` · pièces majorées de ${invoice.parts_markup_percent} %` : ''}
            </p>
            {invoice.auto_warnings?.length > 0 && (
              <ul className="mt-1 list-disc pl-4 text-amber-200/90" data-testid="invoice-auto-warnings">
                {invoice.auto_warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* Invoice number */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-3">
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs text-slate-400 block mb-1">No de facture</label>
              <input type="text" value={invoice.custom_invoice_number || invoice.invoice_number || ''}
                onChange={e => setInvoice(prev => ({...prev, custom_invoice_number: e.target.value}))}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="invoice-number" />
            </div>
          </div>

          {/* Client selector */}
          <div>
            <label className="text-xs text-slate-400 block mb-1">Client</label>
            <select value={invoice.client_id || ''} onChange={e => handleClientChange(e.target.value)}
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
              data-testid="detail-client-select">
              <option value="">-- Selectionner --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {!invoice.client_id && (
              <input type="text" value={invoice.client_name || ''} placeholder="Client ponctuel (nom libre)"
                onChange={e => setInvoice(prev => ({ ...prev, client_name: e.target.value }))}
                className="mt-2 w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                data-testid="detail-client-free-name" />
            )}
          </div>

          {/* Vehicle / Work order info */}
          {invoice.work_order_number && (
            <div className="text-xs text-slate-400 bg-slate-800/50 rounded-lg p-2">
              <span className="text-amber-400">BT:</span> {invoice.work_order_number}
              {invoice.vehicle_info && <> — {invoice.vehicle_info}</>}
            </div>
          )}
        </div>

        {/* Rate presets */}
        {clientPresets.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-amber-400 font-semibold">CHARTE DE PRIX / PRÉRÉGLAGES</p>
            <div className="flex flex-wrap gap-2">
              {clientPresets.map((preset, idx) => (
                <button key={idx} onClick={() => applyPreset(preset)}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium bg-slate-800 border border-amber-500/30 text-amber-300 hover:bg-amber-500/10 transition-colors"
                  data-testid={`preset-${idx}`}>
                  {preset.name} — {preset.value}$
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Line items */}
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <p className="text-xs text-amber-400 font-semibold">LIGNES DE FACTURATION</p>
            <button onClick={addLineItem} className="text-xs text-amber-400 hover:text-amber-300" data-testid="add-line-btn">
              + Ajouter ligne
            </button>
          </div>
          {(invoice.line_items || []).map((item, idx) => (
            <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-2">
              <div className="flex gap-2 items-start">
                <input type="text" value={item.description} placeholder="Description"
                  onChange={e => updateLineItem(idx, 'description', e.target.value)}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  data-testid={`line-desc-${idx}`} />
                <button onClick={() => removeLineItem(idx)} className="text-red-400 hover:text-red-300 p-2">
                  <Trash2 size={14} />
                </button>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-[10px] text-slate-500">Qte</label>
                  <input type="number" step="0.01" value={item.quantity}
                    onChange={e => updateLineItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white text-center focus:border-amber-500 focus:outline-none"
                    data-testid={`line-qty-${idx}`} />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-slate-500">Prix unit.</label>
                  <input type="number" step="0.01" value={item.unit_price}
                    onChange={e => updateLineItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-white text-center focus:border-amber-500 focus:outline-none"
                    data-testid={`line-price-${idx}`} />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-slate-500">Montant</label>
                  <div className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1.5 text-sm text-amber-400 text-center font-medium">
                    {item.amount?.toFixed(2)} $
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Totals */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-3 space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Sous-total</span>
            <span className="text-white">{subtotal.toFixed(2)} $</span>
          </div>
          <div className="flex justify-between items-center text-sm gap-2">
            <label className="text-slate-400 flex items-center gap-2">Réduction fidélité
              <input type="number" min="0" max="100" step="0.5" value={invoice.discount_percent ?? 0}
                onChange={e => setInvoice(prev => ({ ...prev, discount_percent: e.target.value }))}
                className="w-16 bg-slate-800 border border-slate-700 rounded px-1.5 py-0.5 text-xs text-white" data-testid="invoice-discount-percent" /> %
            </label>
            <span className="text-emerald-400" data-testid="invoice-discount-amount">{discount ? `-${discount.toFixed(2)} $` : '—'}</span>
          </div>
          {discount > 0 && (
            <p className="text-[11px] text-emerald-400/80 italic">Sur la facture : « Nous apprécions votre fidélité : une réduction de {discountPct} % vous a été accordée. »</p>
          )}
          {invoice.taxes_enabled && (invoice.applied_taxes || []).map((tax, idx) => (
            <div key={idx} className="flex justify-between text-sm">
              <span className="text-slate-400">{tax.name} ({tax.rate}%)</span>
              <span className="text-white">{(taxable * tax.rate / 100).toFixed(2)} $</span>
            </div>
          ))}
          <div className="flex justify-between text-base font-bold border-t border-slate-700 pt-1 mt-1">
            <span className="text-white">Total</span>
            <span className="text-amber-400">
              {(taxable + (invoice.taxes_enabled ? (invoice.applied_taxes || []).reduce((s, t) => s + taxable * t.rate / 100, 0) : 0)).toFixed(2)} $
            </span>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="text-xs text-slate-400 block mb-1">Notes</label>
          <textarea value={invoice.notes || ''} onChange={e => setInvoice(prev => ({...prev, notes: e.target.value}))}
            rows={2} className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none resize-none"
            data-testid="invoice-notes" />
        </div>

        {/* Action buttons */}
        <div className="space-y-2">
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold"
            onClick={() => saveInvoice()} disabled={saving} data-testid="save-invoice-btn">
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>

          {invoice.status === 'draft' && (
            <div className="flex gap-2">
              <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={submitForApproval} data-testid="submit-approval-btn">
                <Send size={14} className="mr-1" /> Soumettre pour approbation
              </Button>
              <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                onClick={handleDelete} data-testid="delete-invoice-btn">
                <Trash2 size={14} />
              </Button>
            </div>
          )}

          {invoice.status === 'pending_approval' && (
            <p className="text-center text-sm text-amber-400">En attente d'approbation par l'administrateur</p>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================
// TAX CONFIG TAB
// ============================
function TaxConfigTab({ token, onBack }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/atelier-billing/tax-config`, { headers })
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ taxes_enabled: true, taxes: [{ name: 'TPS', rate: 5.0, enabled: true }, { name: 'TVQ', rate: 9.975, enabled: true }] }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSave = async () => {
    try {
      await axios.put(`${API_URL}/api/atelier-billing/tax-config`, config, { headers });
      toast.success('Taxes enregistrees');
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading || !config) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div></div>;

  return (
    <div className="min-h-screen bg-slate-950 pb-20 px-4 pt-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Retour
        </button>
        <h2 className="text-lg font-bold text-white">Configuration des taxes</h2>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">
          {(config.taxes || []).map((tax, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <input type="checkbox" checked={tax.enabled !== false}
                onChange={e => { const t = [...config.taxes]; t[idx] = {...t[idx], enabled: e.target.checked}; setConfig(prev => ({...prev, taxes: t})); }}
                className="accent-amber-500" />
              <span className="text-white text-sm flex-1">{tax.name}</span>
              <input type="number" step="0.001" value={tax.rate}
                onChange={e => { const t = [...config.taxes]; t[idx] = {...t[idx], rate: parseFloat(e.target.value) || 0}; setConfig(prev => ({...prev, taxes: t})); }}
                className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-white text-right" />
              <span className="text-xs text-slate-400">%</span>
            </div>
          ))}
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" onClick={handleSave} data-testid="save-tax-config-btn">
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
}


// ============================
// INVOICE CONFIG TAB
// ============================
function InvoiceConfigTab({ token, onBack }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    axios.get(`${API_URL}/api/atelier-billing/invoice-config`, { headers })
      .then(r => setConfig(r.data))
      .catch(() => setConfig({ company_name: '', company_address: '', company_phone: '', company_email: '', tps_number: '', tvq_number: '' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/atelier-billing/invoice-config`, config, { headers });
      toast.success('Configuration enregistree');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  if (loading || !config) return <div className="min-h-screen bg-slate-950 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div></div>;

  return (
    <div className="min-h-screen bg-slate-950 pb-20 px-4 pt-4">
      <div className="max-w-2xl mx-auto space-y-4">
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 hover:text-white text-sm">
          <ArrowLeft size={16} /> Retour
        </button>
        <h2 className="text-lg font-bold text-white">Configuration facture</h2>
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-4">
          <p className="text-xs text-slate-400">Ces informations apparaissent sur l'entete de vos factures PDF.</p>
          <div className="grid grid-cols-1 gap-3">
            {[
              { key: 'company_name', label: 'Nom entreprise / garage' },
              { key: 'company_address', label: 'Adresse' },
              { key: 'company_phone', label: 'Telephone' },
              { key: 'company_email', label: 'Courriel' },
              { key: 'tps_number', label: 'No TPS' },
              { key: 'tvq_number', label: 'No TVQ' },
            ].map(f => (
              <div key={f.key}>
                <label className="text-xs text-slate-400 mb-1 block">{f.label}</label>
                <input type="text" value={config[f.key] || ''}
                  onChange={e => setConfig(prev => ({...prev, [f.key]: e.target.value}))}
                  className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:border-amber-500 focus:outline-none"
                  data-testid={`config-${f.key}`} />
              </div>
            ))}
          </div>
          <PriceChartSection config={config} setConfig={setConfig} />
          <AtelierLogoField config={config} setConfig={setConfig} />
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-black" onClick={handleSave} disabled={saving}
            data-testid="save-invoice-config-btn">
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
